import { TZDateMini } from '@date-fns/tz';
import {
  ClipboardEventHandler,
  FocusEventHandler,
  FocusEvent as ReactFocusEvent,
  ReactEventHandler,
  KeyboardEventHandler as ReactKeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';

import {
  _parseTimeExpression,
  parseTimeAsTimeValue,
} from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useDateTimeParser } from './useDateTimeParser.js';
import { spinExpressionValue } from './useExpressionSpin.js';
import { useAriaLabelingProps } from '../../../core/hooks/useAriaLabelingProps.js';
import { useControllableState } from '../../../core/hooks/useControllableState.js';
import { isEqualTimeValue } from '../../../core/time/helpers.js';
import { useDateTimePickerStore } from '../../../core/time/timeframeStore/useTimeframeStore.js';
import type { TimeValue } from '../../../core/types/time.js';
import { useDateTimePicker } from '../../date-time-picker/hooks/useDateTimePicker.js';
import {
  formatTimeValue,
  getTimeValue,
} from '../../forms-core/time/timeParser.js';
import { useSelection } from '../../spin-buttons/hooks/useSelection.js';
import type { NumberSpinButtonRef } from '../../spin-buttons/number-spin-button/types.js';
import type { SpinButtonHandler } from '../../spin-buttons/spin-button-group/types.js';
import {
  captureUndoSnapshot,
  isContentModifyingKey,
  normalizeISOInput,
  parseDateOnlyToISO,
  parseTimeOnlyToISO,
  partialDateTimeToISO,
  tryParsePartialDate,
  tryParsePartialTime,
} from '../helpers.js';
import {
  getExpressionCursorOffset,
  restoreExpressionCursorOffset,
} from '../selection-helpers.js';
import type { DateTimeInputProps, UndoSnapshot } from '../types.js';

function isNumberSpinButton(
  handler: SpinButtonHandler | undefined,
): handler is NumberSpinButtonRef {
  return handler?.type === 'number';
}

const DATE_PART_TYPES: Set<Intl.DateTimeFormatPart['type']> = new Set([
  'day',
  'month',
  'year',
]);
const TIME_PART_TYPES: Set<Intl.DateTimeFormatPart['type']> = new Set([
  'hour',
  'minute',
  'second',
  'fractionalSecond',
  'dayPeriod',
]);

/**
 * Inserts plain text at the current cursor position, replacing any active selection.
 */
const insertTextAtCursor = (text: string) => {
  const selection = window.getSelection();
  if (selection?.rangeCount) {
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
};

/**
 * Returns true when at least one non-literal spin button is empty.
 */
const hasAnyEmptySpinButton = (
  handlersRef: RefObject<
    Map<
      keyof Intl.DateTimeFormatPartTypesRegistry,
      SpinButtonHandler | null | undefined
    >
  >,
): boolean =>
  Array.from(handlersRef.current?.entries() ?? []).some(
    ([type, handler]) =>
      handler &&
      type !== 'literal' &&
      handler.element.current?.textContent === '',
  );

/**
 * Pastes text into the expression contenteditable span, either at the cursor
 * position (when the selection is inside the span) or by replacing its content.
 */
const pasteIntoExpressionInput = (
  element: RefObject<HTMLSpanElement | null>,
  text: string,
): void => {
  if (element.current) {
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;

    if (
      range &&
      element.current.contains(range.startContainer) &&
      element.current.contains(range.endContainer)
    ) {
      insertTextAtCursor(text);
    } else {
      element.current.textContent = text;
      moveCursorToEnd(element.current);
    }

    element.current.dispatchEvent(new InputEvent('input', { bubbles: true }));
  }
};

/**
 * Moves the cursor (selection) to the end of the given element's content.
 */
const moveCursorToEnd = (element: HTMLSpanElement | null | undefined) => {
  if (element) {
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(element);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
};

/*
 * A hook that provides the default functionality for the DateTimeInput picker
 */
export const useDateTimeInput = (
  props: DateTimeInputProps,
  expressionInputRef: RefObject<HTMLSpanElement | null>,
) => {
  const {
    disabled,
    readOnly,
    type,
    onChange,
    onLeadingEdge,
    onTrailingEdge,
    dateOnlyPasteAnchor = 'startOfDay',
    onPasteComplete,
    partialTimeAnchorDate,
  }: DateTimeInputProps = props;
  const precision = useDateTimePickerStore((state) =>
    state.precision !== 'day' ? state.precision : 'milliseconds',
  );

  const ariaProps = useAriaLabelingProps(props);
  const { regexDateTimeStart, maxValue, types } = useDateTimeParser({
    type,
  });
  // ? INVESTIGATE: without the generic, building the visreg story viewer and unit-test typechecking fails,
  // ? but the components:build target still succeeds without any issues -> why?
  const [value, setValue] = useControllableState<TimeValue | null>(props);
  const isControlled = props.value !== undefined;
  const [valueType, setValueType] = useState(value?.type ?? 'expression');
  const firstRun = useRef(true);
  // Tracks the previous value content so that a parent re-render that passes a
  // new object reference with the same content does not trigger formatExpressionValue
  // and reset DOM edits that haven't propagated through React state yet.
  const prevValueRef = useRef<TimeValue | null | undefined>(undefined);
  const [expressionEmpty, setExpressionEmpty] = useState(
    !expressionInputRef.current?.textContent,
  );
  const [inputValue, setInputValue] = useState('');
  // Skips no-op setInputValue calls in the [value] effect. Passive-effect
  // writes that React can't bail out on schedule SyncLane commits that
  // accumulate on rapid keypresses.
  const lastInputValueRef = useRef(inputValue);
  lastInputValueRef.current = inputValue;
  const pasteUndoRef = useRef<{
    snapshot: UndoSnapshot;
    focusTarget: Element | null;
    expressionCursorOffset?: number;
  } | null>(null);

  useEffect(() => {
    return () => {
      pasteUndoRef.current = null;
    };
  }, []);

  // Tracks the last valid absoluteDate to pass a stable, parseable ISO string
  // to useDateTimePicker while props.value temporarily has absoluteDate:'' during
  // partial spinbutton editing. Keeps useDateTimePicker.value prop unchanged so
  // its internal useEffect doesn't re-run and clear the spinbuttons.
  const lastValidAbsoluteDateRef = useRef<string | null>(
    value?.type === 'iso8601' && value.absoluteDate ? value.absoluteDate : null,
  );
  // True only during the requestAnimationFrame window opened by switchToExpressionMode.
  // Used to suppress stale spinbutton onChange callbacks that fire while the mode
  // transition is in flight, without blocking legitimate restore/undo calls.
  const isSwitchingToExpressionRef = useRef(false);

  // Use valueType (local state) not value.type (controlled prop) as the gate —
  // the controlled prop can temporarily become type:'expression' when the store
  // echoes back a partial invalid value for validation while we're still in
  // spinbutton mode.
  if (
    valueType === 'iso8601' &&
    value?.type === 'iso8601' &&
    value.absoluteDate
  ) {
    lastValidAbsoluteDateRef.current = value.absoluteDate;
  }
  const spinButtonValue =
    valueType === 'iso8601' ? lastValidAbsoluteDateRef.current : null;

  // Stores the date parts filled in by a partial paste so the focus can target
  // the first spin button that was not filled.
  const pendingFocusPartsRef = useRef<Set<
    keyof Intl.DateTimeFormatPartTypesRegistry
  > | null>(null);

  const {
    spinButtons,
    handlers: spinButtonHandlers,
    empty: spinButtonsEmpty,
    applyDateTime,
    firstSpinButtonHandler,
  } = useDateTimePicker({
    type,
    precision,
    disabled,
    readOnly,
    valueType,
    value: spinButtonValue,
    onChange: (newIsoDateTime) => {
      const newValue = getTimeValue(newIsoDateTime ?? undefined, precision);

      if (newValue === null || newValue === undefined) {
        return;
      }

      // Suppress stale spinbutton callbacks that fire while switchToExpressionMode
      // is transitioning. Any other caller (e.g. undo restore) is allowed through.
      if (isSwitchingToExpressionRef.current) {
        return;
      }

      if (expressionInputRef.current) {
        expressionInputRef.current.textContent = '';
      }

      // If undo (or another caller) pushes a value while we're in expression mode,
      // switch back to spinbutton mode now.
      if (valueType !== 'iso8601') {
        setValueType('iso8601');
      }

      // Forward invalid values (empty string or unparseable partial locale string)
      // to onChange so the store validates and shows errors, but skip setValue so
      // props.value stays stable and spinbuttons are not cleared.
      if (newIsoDateTime === '' || newValue?.absoluteDate === '') {
        onChange?.(newValue);
        return;
      }

      setInputValue(newValue.value);
      // only update the value if the newTimeValue differs from the prev. one
      setValue((prevValue) =>
        isEqualTimeValue(prevValue, newValue) ? prevValue : newValue,
      );
    },
    ...ariaProps,
  });

  const empty = useMemo(
    () => (valueType === 'expression' ? expressionEmpty : spinButtonsEmpty),
    [valueType, expressionEmpty, spinButtonsEmpty],
  );

  // Shared state reset when switching (back) to expression mode.
  const resetToExpressionMode = useCallback(
    (expressionEmpty = true) => {
      setValueType('expression');
      setValue(null);
      setInputValue('');
      setExpressionEmpty(expressionEmpty);
      lastValidAbsoluteDateRef.current = null;
    },
    [setValue],
  );

  // Tracks the last absoluteDate seen by the controlled-value effect.
  // Used to distinguish a genuine value re-application (absoluteDate changed,
  // e.g. from '' back to a valid ISO string) from an outdated reference change
  // (the store re-parsed a sibling field and created a new object with the
  // same absoluteDate).
  const prevAbsoluteDateRef = useRef<string | null>(null);

  // Called on onBlur to sanitize the current expression. Declared here (before
  // the [value] useEffect below) so that React Compiler's immutability analysis
  // can confirm the reference is available at the time the effect is registered.
  const formatExpressionValue = useCallback(
    (value: TimeValue | null) => {
      if (!expressionInputRef.current) {
        return;
      }
      const formattedValue = value ? formatTimeValue(value) : '';
      const currentValue = formatTimeValue(
        expressionInputRef.current?.textContent ?? '',
      );

      // only update if the current value differs from the new value
      // don't update the textnode while typing but allow e.g. pasting a new expression
      if (currentValue !== formattedValue) {
        expressionInputRef.current.textContent = formattedValue;
      }
    },
    // no use in adding the expressionInputRef to the dep. array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Keep a ref to the latest spinButtonHandlers so that focus helpers
  // inside requestAnimationFrame always use the current map.
  const spinButtonHandlersRef = useRef(spinButtonHandlers);
  spinButtonHandlersRef.current = spinButtonHandlers;

  // switch input mode based on controlled value change
  useEffect(() => {
    const prevValue = prevValueRef.current;
    prevValueRef.current = value;

    const applyInputValue = (next: string) => {
      if (lastInputValueRef.current === next) {
        return;
      }
      lastInputValueRef.current = next;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInputValue(next);
    };

    if (value?.type === 'iso8601' && !spinButtonsEmpty) {
      applyInputValue(value.value);
    }

    // ignore if in uncontrolled mode
    if (!isControlled || typeof value === 'string') {
      return;
    }

    const prevAbsoluteDate = prevAbsoluteDateRef.current;
    prevAbsoluteDateRef.current = value?.absoluteDate ?? null;

    // reset input if value is null
    if (!value) {
      setValueType('expression');
      formatExpressionValue(null);
      const input = expressionInputRef.current?.textContent;
      setExpressionEmpty(input === '');
      applyDateTime(null);
      lastValidAbsoluteDateRef.current = null;
      return;
    }

    // Skip values with empty absoluteDate: partial/invalid intermediate states
    // forwarded to the store for validation only. The spinbuttons manage their
    // own display; reacting here would switch to 'expression' mode and call
    // formatExpressionValue, clearing the display.
    if (value.absoluteDate === '') {
      return;
    }

    // apply value to expression
    // the spinbutton update is handled within the useSpinButton hook internally
    if (valueType !== value.type) {
      setValueType(value.type);
    }
    applyInputValue(value.value);
    if (value.type === 'expression') {
      // Only reformat the DOM when the expression content has actually changed.
      // A parent re-render that produces a new object reference with the same
      // value (e.g. due to a sibling input's state update) must not overwrite
      // DOM edits (e.g. partial cross-expression deletion) that haven't yet
      // propagated back through React state.
      if (!isEqualTimeValue(prevValue ?? null, value)) {
        formatExpressionValue(value);
      }
      setExpressionEmpty(value.value === '');
    } else if (
      value.type === 'iso8601' &&
      hasAnyEmptySpinButton(spinButtonHandlersRef) &&
      prevAbsoluteDate !== value.absoluteDate
    ) {
      applyDateTime(value.absoluteDate);
    }
    // only react to outside value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Focuses the first spin button that is still empty, or the last spin button when
  // everything is filled in.
  const focusFirstUnfilledSpinButton = useCallback(
    (filledParts: Set<keyof Intl.DateTimeFormatPartTypesRegistry>) => {
      const handlers = spinButtonHandlersRef.current;
      for (const [type, handler] of handlers) {
        if (
          handler &&
          type !== 'literal' &&
          !filledParts.has(
            type as keyof Intl.DateTimeFormatPartTypesRegistry,
          ) &&
          handler.element.current?.textContent === ''
        ) {
          handler.element.current?.focus();
          return;
        }
      }
      // Fallback: focus the last spin button when everything is filled.
      let lastHandler: SpinButtonHandler | null = null;
      for (const [, handler] of handlers) {
        if (handler) {
          lastHandler = handler;
        }
      }
      lastHandler?.element?.current?.focus();
    },
    [],
  );

  // Focus the first unfilled spin button after a partial paste triggers a mode switch.
  useEffect(() => {
    const filledParts = pendingFocusPartsRef.current;
    if (!filledParts || valueType !== 'iso8601') {
      return;
    }
    pendingFocusPartsRef.current = null;

    requestAnimationFrame(() => {
      focusFirstUnfilledSpinButton(filledParts);
    });
  }, [valueType, focusFirstUnfilledSpinButton]);

  const { selectAll: expressionInputSelectAll } =
    useSelection(expressionInputRef);

  // handles input events on the expression contenteditable span
  const expressionInputHandler: ReactEventHandler<HTMLSpanElement> =
    useCallback(() => {
      if (!expressionInputRef.current) {
        return;
      }

      const input = expressionInputRef.current.textContent ?? '';

      // Remove <br> added by browser in contenteditable to ensure placeholder works
      if (expressionInputRef.current.innerHTML === '<br>') {
        expressionInputRef.current.innerHTML = '';
      }

      // fallback to expression if text content is removed
      if (input === '') {
        setValueType('expression');
        setValue(null);
        setExpressionEmpty(true);
        return;
      }

      setExpressionEmpty(false);

      // if the input starts like a valid datetime for the current locale, switch to the datetime picker
      const [, carryOver] = regexDateTimeStart.exec(input) ?? [];
      if (Number.parseInt(carryOver) <= maxValue) {
        setValueType('iso8601');

        // clear all spinbuttons
        applyDateTime(null);
        lastValidAbsoluteDateRef.current = null;

        // get a cleaned and sorted list of spinbutton handlers
        const sortedHandlers = types
          .filter((type) => type !== 'literal')
          .map((type) => spinButtonHandlers.get(type))
          .filter(Boolean) as SpinButtonHandler[];
        const [carryOverSB, focusSB] = sortedHandlers;

        // carry over entered value to the next number spinbutton
        isNumberSpinButton(carryOverSB) &&
          carryOverSB.applyText(carryOver, true);

        // clear expression input content
        expressionInputRef.current.textContent = '';

        // set focus after switch to expression mode happened
        requestAnimationFrame(() => {
          // set focus to second spinbutton
          focusSB?.element.current?.focus();
        });
        return;
      }

      // If the user types a number followed by ":", switch to the datetime picker
      // with the anchor date pre-filled and the typed hour applied, then focus the
      // minute spin button so the user can continue typing the time.
      // The anchor date defaults to today but can be overridden via `partialTimeAnchorDate`
      if (type !== 'date') {
        const colonMatch = /^(\d{1,2}):$/.exec(input);
        if (colonMatch) {
          const hour = Number.parseInt(colonMatch[1]);
          if (hour <= 23) {
            const base = partialTimeAnchorDate
              ? new TZDateMini(partialTimeAnchorDate, getTimezone())
              : TZDateMini.tz(getTimezone());
            const iso = TZDateMini.tz(
              getTimezone(),
              base.getFullYear(),
              base.getMonth(),
              base.getDate(),
              hour,
              0,
              0,
              0,
            ).toISOString();

            const partsToUpdate = new Set<
              keyof Intl.DateTimeFormatPartTypesRegistry
            >(['day', 'month', 'year', 'hour']);

            setValueType('iso8601');
            expressionInputRef.current.textContent = '';
            applyDateTime(iso, partsToUpdate);

            requestAnimationFrame(() => {
              focusFirstUnfilledSpinButton(partsToUpdate);
            });
            return;
          }
        }
      }

      // If the copied text contains the formatted date in the user's locale (e.g.,
      // "12/24/2024" or "25.12.2024"), switch to iso8601 mode and apply the parsed date
      // fields to the spin buttons.
      const typedPartialDate = tryParsePartialDate(input);
      if (typedPartialDate) {
        const dateResult = partialDateTimeToISO(typedPartialDate, null);
        if (dateResult) {
          setValueType('iso8601');
          expressionInputRef.current.textContent = '';
          applyDateTime(dateResult.iso, dateResult.partsToUpdate);

          requestAnimationFrame(() =>
            focusFirstUnfilledSpinButton(dateResult.partsToUpdate ?? new Set()),
          );
          return;
        }
      }

      // if input is a valid expression, update the internal value state
      const newValue = getTimeValue(input, precision) ?? null;

      if (newValue?.absoluteDate === '') {
        onChange?.(newValue);
      }
      // only update the value if the newTimeValue differs from the prev. one
      setValue((prevValue) =>
        isEqualTimeValue(prevValue, newValue) ? prevValue : newValue,
      );

      setInputValue(input);
    }, [
      expressionInputRef,
      regexDateTimeStart,
      maxValue,
      partialTimeAnchorDate,
      precision,
      type,
      setValue,
      applyDateTime,
      focusFirstUnfilledSpinButton,
      types,
      spinButtonHandlers,
      onChange,
    ]);

  // select expression text content on focus
  const expressionOnFocusHandler: FocusEventHandler<HTMLSpanElement> =
    useCallback(
      (event: ReactFocusEvent<HTMLSpanElement>) => {
        // If the current selection spans multiple nodes (e.g. a drag-select or
        // Cmd+A across multiple spin buttons / expression inputs), don't collapse
        // it to just this expression input.
        const selection = window.getSelection();
        const selectionSpansMultipleNodes =
          selection !== null &&
          selection.anchorNode !== selection.focusNode &&
          selection.toString().length > 0;

        // Also skip if we are in the middle of a programmatic focus + setBaseAndExtent
        // (guarded by data-restoring-selection on the .strato-inputgroup root).
        const isRestoringSelection =
          (event.target as HTMLElement)
            .closest('.strato-inputgroup')
            ?.hasAttribute('data-restoring-selection') ?? false;

        // Skip if the user already has a non-collapsed selection inside this
        // expression input (e.g. drag-select or Shift+arrow within one field).
        // Calling selectAll() here would override that intended partial selection.
        const expressionInputEl = event.target as HTMLElement;
        const hasPartialSelectionWithin =
          selection !== null &&
          !selection.isCollapsed &&
          expressionInputEl.contains(selection.anchorNode) &&
          expressionInputEl.contains(selection.focusNode);

        if (
          selectionSpansMultipleNodes ||
          isRestoringSelection ||
          hasPartialSelectionWithin
        ) {
          return;
        }

        expressionInputSelectAll();
      },
      [expressionInputSelectAll],
    );

  // sanitize textnode if content is an expression
  const expressionOnBlurHandler: FocusEventHandler<HTMLSpanElement> =
    useCallback(() => {
      if (expressionInputRef.current && value?.type === 'expression') {
        const currentExpressionInput =
          expressionInputRef.current.textContent ?? '';
        const isValidExpression = _parseTimeExpression(
          currentExpressionInput.trim(),
        );

        if (isValidExpression) {
          expressionInputRef.current.textContent = formatTimeValue(value);
        }
      }

      // eslint annoyingly wants to add the ref to the dependency list
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

  // Shared paste logic used by both the DOM paste event handler and the
  // imperative pasteText method on DateTimeInputRef. Returns true when the
  // paste was handled (caller should preventDefault), false to let native
  // paste proceed (step 4: digits in iso8601 mode).
  const pasteText = useCallback(
    (text: string, skipUndoCapture = false): boolean => {
      if (readOnly) {
        return false;
      }

      let trimmedData = normalizeISOInput(text.trim());
      if (!trimmedData) {
        return true;
      }

      // Pre-parse locale-specific partial date/time for normalization and step 5.
      const partialDate = tryParsePartialDate(trimmedData);
      const partialTime = tryParsePartialTime(trimmedData);
      const partialResult =
        partialDate || partialTime
          ? partialDateTimeToISO(partialDate, partialTime)
          : null;

      // When the result is complete (no partsToUpdate), normalize to ISO so
      // the existing ISO checks (steps 1–3) can handle it.
      if (partialResult && !partialResult.partsToUpdate) {
        trimmedData = partialResult.iso;
      }

      // Capture undo snapshot before any state mutations unless the caller
      // already owns the snapshot (e.g. range-level paste handler).
      if (!skipUndoCapture) {
        const snapshot = captureUndoSnapshot(
          spinButtonHandlers,
          expressionInputRef.current,
          value,
          valueType,
        );

        pasteUndoRef.current = {
          snapshot,
          focusTarget: document.activeElement,
          expressionCursorOffset: expressionInputRef.current
            ? getExpressionCursorOffset(expressionInputRef.current)
            : undefined,
        };
      }

      // 1. Date-only paste (e.g. "2025-01-01")
      // 2. Time-only paste (e.g. "02:00Z", "14:30:00")
      const dateOnly = parseDateOnlyToISO(trimmedData, dateOnlyPasteAnchor);
      const timeOnly = parseTimeOnlyToISO(trimmedData);
      const date = dateOnly ?? timeOnly;

      if (date) {
        setValueType('iso8601');
        // When spinbuttons are empty (expression mode), apply all parts so the
        // full date is filled in.  When parts are already populated, only
        // overwrite the date or time group that matches the pasted value.
        let partsFilter: Set<Intl.DateTimeFormatPart['type']> | undefined;
        if (!spinButtonsEmpty) {
          partsFilter = dateOnly ? DATE_PART_TYPES : TIME_PART_TYPES;
        }
        applyDateTime(date, partsFilter);

        // When only a subset of parts was applied, focus the first empty spin
        // button instead of advancing. When all parts were applied skip the check.
        if (partsFilter && hasAnyEmptySpinButton(spinButtonHandlersRef)) {
          focusFirstUnfilledSpinButton(partsFilter);
        } else {
          onPasteComplete?.();
        }

        return true;
      }

      // 3. Full ISO datetime or expression (e.g. "2025-01-01T02:00Z", "now()-1h")
      const nextValue = parseTimeAsTimeValue(
        trimmedData,
        TZDateMini.tz(getTimezone()).getTime(),
        precision,
      );

      if (nextValue) {
        setValueType(nextValue.type);
        if (nextValue.type === 'expression') {
          formatExpressionValue(nextValue);
          moveCursorToEnd(expressionInputRef.current);
          setValue(nextValue);
        } else {
          applyDateTime(nextValue.absoluteDate);
        }
        onPasteComplete?.();
        return true;
      }

      // 4. Allow numbers to be pasted into the spin buttons
      if (valueType === 'iso8601' && /^\d+$/.test(trimmedData)) {
        return false;
      }

      // 5. Partial date/time paste (e.g. "25.12", "14:30", "25.12.2024 14:30")
      // partialResult was pre-computed above the ISO checks.
      if (partialResult?.partsToUpdate) {
        const { iso, partsToUpdate } = partialResult;
        setValueType('iso8601');
        applyDateTime(iso, partsToUpdate);

        // Clear any spin button that was not explicitly present in the pasted
        // text (i.e. was a placeholder token or simply absent). This ensures
        // paste always overwrites: fields not carried by the pasted value are
        // emptied rather than left at their previous content.
        for (const [key, handler] of spinButtonHandlersRef.current) {
          if (handler && !partsToUpdate.has(key)) {
            handler.clearValue(true);
          }
        }

        if (hasAnyEmptySpinButton(spinButtonHandlersRef)) {
          requestAnimationFrame(() => {
            focusFirstUnfilledSpinButton(partsToUpdate);
          });
        } else {
          onPasteComplete?.();
        }

        return true;
      }

      // 6. Fallback: switch to expression mode and paste the raw string.
      if (valueType === 'expression') {
        if (expressionInputRef.current) {
          pasteIntoExpressionInput(expressionInputRef, trimmedData);
        }
      } else {
        setValueType('expression');
        if (expressionInputRef.current) {
          expressionInputRef.current.textContent = trimmedData;
          expressionInputRef.current.focus();
          moveCursorToEnd(expressionInputRef.current);
          expressionInputRef.current.dispatchEvent(
            new InputEvent('input', { bubbles: true }),
          );
        }
      }

      return true;
    },
    [
      readOnly,
      spinButtonHandlers,
      expressionInputRef,
      value,
      dateOnlyPasteAnchor,
      precision,
      valueType,
      setValue,
      onPasteComplete,
      formatExpressionValue,
      applyDateTime,
      spinButtonsEmpty,
      focusFirstUnfilledSpinButton,
    ],
  );

  // clipboard paste handler. apply clipboard content if formatted correctly
  const onPaste: ClipboardEventHandler<HTMLSpanElement> = useCallback(
    (event) => {
      const data = event.clipboardData?.getData('text/plain');
      if (!data) {
        event.preventDefault();
        return;
      }
      if (pasteText(data)) {
        event.preventDefault();
      }
    },
    [pasteText],
  );

  // Ctrl+Z / Cmd+Z handler to undo a paste operation.
  const onPasteUndoKeyDown: ReactKeyboardEventHandler<HTMLSpanElement> =
    useCallback(
      (event) => {
        const isCtrlZ = (event.ctrlKey || event.metaKey) && event.key === 'z';

        if (!isCtrlZ) {
          // Invalidates the paste snapshot for any other input.
          if (pasteUndoRef.current && isContentModifyingKey(event)) {
            pasteUndoRef.current = null;
          }
          return;
        }

        if (!pasteUndoRef.current) {
          // No paste to undo — block native browser contenteditable undo.
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        const { snapshot, focusTarget, expressionCursorOffset } =
          pasteUndoRef.current;
        pasteUndoRef.current = null;

        event.preventDefault();
        event.stopPropagation();

        // Restore value type first so the correct UI mode is rendered.
        setValueType(snapshot.valueType);

        if (snapshot.valueType === 'iso8601') {
          // Restore spin button values from the snapshot.
          snapshot.spinButtonTexts.forEach((text, type) => {
            const handler = spinButtonHandlers.get(type) ?? null;
            if (!handler) {
              return;
            }
            if (text === '') {
              handler.clearValue(true);
            } else if (handler.type === 'number') {
              handler.applyText(text, true);
            } else {
              handler.searchValue(text[0] ?? '', true);
            }
          });
        } else {
          // Restore expression text content.
          if (expressionInputRef.current) {
            expressionInputRef.current.textContent = snapshot.expressionText;
          }
          setExpressionEmpty(snapshot.expressionText === '');
        }

        setValue(snapshot.value);
        setInputValue(snapshot.value?.value ?? '');

        requestAnimationFrame(() => {
          // Restore focus after the mode-switch re-render.
          if (
            focusTarget instanceof HTMLElement &&
            focusTarget !== document.activeElement
          ) {
            focusTarget.focus();
          }

          // Restore cursor position in the expression input
          if (
            snapshot.valueType === 'expression' &&
            expressionCursorOffset !== undefined &&
            expressionInputRef.current
          ) {
            restoreExpressionCursorOffset(
              expressionInputRef.current,
              expressionCursorOffset,
            );
          }
        });
      },
      [
        spinButtonHandlers,
        expressionInputRef,
        setValue,
        setValueType,
        setExpressionEmpty,
        setInputValue,
      ],
    );

  // Handle keyboard events on the expression input: prevent Enter and spin on ArrowUp/ArrowDown
  const expressionOnKeyDownHandler: ReactKeyboardEventHandler<HTMLSpanElement> =
    useCallback(
      (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          return;
        }

        if (
          onLeadingEdge &&
          event.key === 'Backspace' &&
          (expressionInputRef.current?.textContent ?? '') === ''
        ) {
          event.preventDefault();
          requestAnimationFrame(() => onLeadingEdge?.());
          return;
        }

        if (
          onTrailingEdge &&
          event.key === 'Delete' &&
          (expressionInputRef.current?.textContent ?? '') === ''
        ) {
          event.preventDefault();
          requestAnimationFrame(() => onTrailingEdge?.());
          return;
        }

        if (event.key === 'ArrowLeft' && onLeadingEdge) {
          const selection = window.getSelection();
          // If cursor is at the start (offset 0), navigate to the previous input
          if (selection?.anchorOffset === 0 && selection?.focusOffset === 0) {
            onLeadingEdge();
            event.preventDefault();
            return;
          }
        }

        if (event.key === 'ArrowRight') {
          const selection = window.getSelection();
          const textContent = expressionInputRef.current?.textContent ?? '';
          // If cursor is at the end, navigate to the next input
          if (selection?.anchorOffset === textContent.length) {
            onTrailingEdge?.();
            event.preventDefault();
            return;
          }
        }

        if (
          (event.key === 'ArrowUp' || event.key === 'ArrowDown') &&
          !disabled &&
          !readOnly
        ) {
          const element = expressionInputRef.current;
          if (!element) {
            return;
          }

          const currentText = element.textContent ?? '';
          if (!currentText.trim()) {
            return;
          }

          const direction: 1 | -1 = event.key === 'ArrowUp' ? 1 : -1;
          const newText = spinExpressionValue(currentText, direction);

          if (newText === null) {
            return;
          }

          event.preventDefault();

          // Preserve cursor position
          const selection = window.getSelection();
          const cursorOffset = selection?.focusOffset ?? 0;

          // Update the text content and fire the input handler
          element.textContent = newText;

          // Restore cursor position (clamped to new text length)
          requestAnimationFrame(() => {
            const textNode = element.firstChild;
            if (textNode && selection) {
              const newOffset = Math.min(cursorOffset, newText.length);
              const range = document.createRange();
              range.setStart(textNode, newOffset);
              range.collapse(true);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          });

          // Trigger the input handler to propagate the value change
          element.dispatchEvent(new Event('input', { bubbles: true }));
        }
      },
      [onLeadingEdge, expressionInputRef, disabled, readOnly, onTrailingEdge],
    );

  // focus the first spinbutton or the expression input based on the current input mode
  const focus = useCallback(() => {
    switch (valueType) {
      case 'expression':
        expressionInputRef.current?.focus();
        break;
      case 'iso8601':
        firstSpinButtonHandler?.element?.current?.focus();
        break;
    }
    // no need to pass the refs as dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueType, firstSpinButtonHandler]);

  /**
   * Clears all spin buttons, switches to expression mode, and seeds the
   * expression input with the provided text. Used when a bulk-delete leaves
   * exactly one spin button filled — that remaining value is carried over as
   * the expression seed rather than leaving a partial date.
   */
  const switchToExpressionMode = useCallback(
    (text: string) => {
      // setExpressionValue is an imperative expression-mode transition. Do not
      // parse the text through getTimeValue here: numeric fallback text such as
      // "01" can otherwise be treated as an ISO value and switch the input back
      // to spin-button mode while a bulk clear is still settling.
      const parsedValue = parseTimeAsTimeValue(
        text,
        TZDateMini.tz(getTimezone()).getTime(),
        precision,
      );
      const newValue: TimeValue | null =
        text !== ''
          ? {
              absoluteDate: parsedValue?.absoluteDate ?? '',
              value: text,
              type: 'expression',
            }
          : null;

      for (const handler of spinButtonHandlers.values()) {
        handler?.clearValue(false);
        if (handler?.element.current) {
          handler.element.current.textContent = '';
        }
      }

      setInputValue(text);

      if (valueType === 'expression') {
        // Already in expression mode — apply synchronously so the next
        // keypress is not dropped by a deferred rAF write.
        setValue(newValue);
        setExpressionEmpty(text === '');
        if (expressionInputRef.current) {
          expressionInputRef.current.textContent = text;
        }
        return;
      }

      // Suppress stale spinbutton callbacks during the transition;
      // cleared after the rAF so undo calls are not blocked.
      isSwitchingToExpressionRef.current = true;
      // Update the backing value synchronously. If this waits for rAF, React 19
      // can flush the previous ISO value first and rehydrate the cleared spin
      // buttons before the expression input receives its fallback text.
      setValue(newValue);
      setExpressionEmpty(text === '');
      setValueType('expression');
      requestAnimationFrame(() => {
        if (expressionInputRef.current) {
          expressionInputRef.current.textContent = text;
        }
        isSwitchingToExpressionRef.current = false;
      });
    },
    [expressionInputRef, precision, setValue, spinButtonHandlers, valueType],
  );

  // first initialization of the uncontrolled expression value on component mount
  useEffect(() => {
    if (isControlled || value?.type !== 'expression') {
      return;
    }
    formatExpressionValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if the spinbuttons are empty ...
  useEffect(() => {
    // Don't run the empty spinbutton check on component mount.
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }

    // ... switch to expression input
    if (valueType === 'iso8601' && spinButtonsEmpty) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      resetToExpressionMode();

      // wait until the expression input is visible to set the focus
      setTimeout(() => {
        expressionInputRef?.current?.focus();
      });
    }
    // only react on empty state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinButtonsEmpty]);

  useEffect(() => {
    if (valueType !== 'expression') {
      return;
    }

    if (empty) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInputValue('');
      return;
    }

    if (expressionInputRef.current?.textContent) {
      setInputValue(expressionInputRef.current?.textContent); // fixes validating initial expression value in the DateTimeRangePicker
    }
    // valueType can be excluded because empty already depends on it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empty]);

  return {
    empty,
    spinButtons,
    spinButtonHandlers,
    value,
    valueType,
    inputValue,
    expressionInputHandler,
    expressionOnFocusHandler,
    expressionOnBlurHandler,
    expressionOnKeyDownHandler,
    onPaste,
    pasteText,
    onPasteUndoKeyDown,
    clearPasteUndo: () => {
      pasteUndoRef.current = null;
    },
    focus,
    switchToExpressionMode,
    setInputValue,
  };
};
